/* eslint-disable array-callback-return */
// third-party
import { createSlice } from '@reduxjs/toolkit';
import { filterQueryString } from 'helpers/objectFunctions';

// project imports
import { ARYABHATTA_API_DOMAIN_V1 } from 'config';
import { devConsolelog } from 'helpers/utilityFunctions';
import axios from 'utils/axios';
import { dispatch } from '../index';
import { openSnackbar } from './snackbar';
import {
    ACM_CONSTANT,
    BMS_CONSTANT,
    BOOKINGS,
    BOOKING_STATUS_CODE_PACKAGE_BOOKING,
    BOOKING_TYPE_REMITTANCE,
    OC_SYSTEM_CONSTANT,
    TRAVELERS_CONSTANT,
    V1_CONSTANT,
    rowsPerPagePaginationAlt
} from 'store/constant';
import { extractErrorMessage } from 'views/pages/create-quote/utilities';
import { setTicketData } from './openCloseTickets';

// ----------------------------------------------------------------------

const DEFAULT_ERROR_MESSAGE = 'Something Went Wrong';

const initialState = {
    error: null,
    data: {},
    autosuggestAssigneeData: [],
    selectedMemberData: {},
    selectedTicketData: {},
    bookingData: {},
    requirementData: {},
    filters: {
        // status: [{ id: 'open', name: 'open' }]
        // query_type: [{ id: 2, name: 2 }]
    },
    commentsData: {
        commentsList: [],
        nextPageUrl: '',
        isScrollToBottom: true
    },
    internalCommentsData: {
        commentsList: [],
        nextPageUrl: '',
        isScrollToBottom: true
    },
    isLoading: false,
    additionalFiltersData: {},
    chatView: false,
    // Information about traveler such as onTrip number and traveler number loading
    onTripInformationLoading: false,
    onTripNumber: '',
    travelerNumber: '',
    numberIsdCode: '',
    internalCommentsLoading: false,
    selectedTicketDataChildTicket: null,
    selectedTicketDataChildTicketFetching: false,
    isVip: false
};

const slice = createSlice({
    name: 'internalCommenting',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        setClearBookingData(state) {
            state.bookingData = {};
            state.commentsData = {
                commentsList: [],
                nextPageUrl: '',
                isScrollToBottom: true
            };
        },

        // setData
        setData(state, action) {
            const {
                count = 0,
                next = null,
                previous = null,
                results = {},
                page = 0,
                page_size: pageSize = rowsPerPagePaginationAlt
            } = action.payload;
            const { utilities = {}, open_close_system: ticketsList = [] } = results || {};
            const { filters = [] } = utilities || {};
            devConsolelog('category data', filters[2]);
            state.data.count = count;
            state.data.next = next;
            state.data.previous = previous;
            state.data.utilities = utilities;
            state.data.ticketsList = ticketsList;
            state.data.currentPage = page;
            state.data.page_size = pageSize;
        },

        setFilters(state, action) {
            devConsolelog('data in state filters', action.payload, state.filters);
            state.filters = action.payload;
        },

        setAutosuggestAssigneeData(state, action) {
            const { results = [] } = action.payload;

            const autosuggestDropdownData = results.map((data) => {
                const { id = '', first_name: firstName = '', last_name: lastName = '', email = '', username = '' } = data || {};

                return {
                    firstName,
                    lastName,
                    email,
                    username,
                    id,
                    label: `${id} | ${username} | ${email}`
                };
            });

            state.autosuggestAssigneeData = autosuggestDropdownData;
        },

        setMemberDetails(state, action) {
            state.selectedMemberData = action.payload;
        },

        setRequirementData(state, action) {
            const { results = {} } = action.payload;
            const { data = {} } = results || {};
            const { buyer = {}, buyer_organization: buyerOrganization = '' } = data || {};
            state.requirementData = data || {};
            state.selectedMemberData = buyer;
            state.selectedMemberData.organization_code = buyerOrganization;
        },

        setBookingData(state, action) {
            const { buyer = {} } = action.payload || {};
            state.bookingData = action.payload;
            state.selectedMemberData = buyer;
        },

        setIsLoading(state, action) {
            const { loadingField = '', isLoading = false } = action.payload || {};
            state[loadingField] = isLoading;
        },

        setSelectedTicketData(state, action) {
            state.selectedTicketData = action.payload;
        },

        setCommentsData(state, action) {
            const { data = {}, isRefresh = false } = action.payload;
            const { next = null, results = [] } = data || {};
            if (isRefresh) {
                state.commentsData.commentsList = results.reverse();
                state.commentsData.isScrollToBottom = true;
            } else {
                state.commentsData.commentsList = [...results.reverse(), ...state.commentsData.commentsList];
                state.commentsData.isScrollToBottom = false;
            }
            state.commentsData.nextPageUrl = next;
        },

        setPostCommentsData(state, action) {
            state.commentsData.commentsList = [...state.commentsData.commentsList, action.payload];
            state.commentsData.isScrollToBottom = true;
        },

        setDynamicConversationObj(state, action) {
            // direct ticket code link (calling api for that particualar ticket code and storing in selected ticket data)
            const { results = {} } = action.payload;
            const { open_close_system: ocSystem = {} } = results || {};
            state.selectedTicketData = ocSystem?.[0];
        },

        setAdditionalFiltersData(state, action) {
            const newObj = { ...state.additionalFiltersData };
            state.additionalFiltersData = { ...newObj, ...action.payload };
        },
        setViewMode(state, action) {
            state.chatView = action.payload;
        },
        clearChat(state, action) {
            state.commentsData = {
                commentsList: [],
                nextPageUrl: '',
                isScrollToBottom: true
            };
        },
        clearLeftChatList(state) {
            state.ticketsList = [];
        },
        setOnTripInformationLoading(state) {
            state.onTripInformationLoading = true;
        },
        setOnTripNumberAndTravelerInformation(state, action) {
            const { onTripNumber = '', travelerNumber = '', numberIsdCode = '', isVip = false } = action.payload;
            state.onTripNumber = onTripNumber;
            state.travelerNumber = travelerNumber;
            state.numberIsdCode = numberIsdCode;
            state.isVip = isVip;
        },
        setAllTicketData(state, action) {
            const { results = {}, next = null, count = 0 } = action.payload;
            const { open_close_system: newTickets = [], utilities = {} } = results || {};

            const { oc_tickets_count: ticketCount } = utilities || {};

            // Overwrite the existing tickets list with the new page's data
            state.allTicketData = {
                results: { open_close_system: newTickets },
                next,
                ticketCount,
                count
            };
        },
        setInternalCommentsData(state, action) {
            const { data = {}, isRefresh = false } = action.payload;
            const { next = null, results = [] } = data || {};
            if (isRefresh) {
                state.internalCommentsData.commentsList = results.reverse();
                state.internalCommentsData.isScrollToBottom = true;
            } else {
                state.internalCommentsData.commentsList = [...results.reverse(), ...state.internalCommentsData.commentsList];
                state.internalCommentsData.isScrollToBottom = false;
            }
            state.internalCommentsData.nextPageUrl = next;
        },
        setPostInternalCommentsData(state, action) {
            state.internalCommentsData.commentsList = [...state.internalCommentsData.commentsList, action.payload];
            state.internalCommentsData.isScrollToBottom = true;
        },
        setSelectedTicketDataChildTicketCode(state, action) {
            state.selectedTicketDataChildTicket = action.payload;
        },
        setSelectedTicketDataChildTicketCodeFetchingLoader(state, action) {
            state.selectedTicketDataChildTicketFetching = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;
export const {
    setViewMode,
    setOnTripInformationLoading,
    setOnTripNumberAndTravelerInformation,
    setInternalCommentsData,
    setIsLoading,
    setPostInternalCommentsData,
    setAllTicketData,
    setSelectedTicketDataChildTicketCode,
    setSelectedTicketDataChildTicketCodeFetchingLoader
} = slice.actions;
// ----------------------------------------------------------------------

export function getAllTicketDataReducer(requestTypeCode = '', requestTypeKey = '', page = 1, status = 'open', rowsPerPage = 20) {
    return async (dispatch) => {
        dispatch(
            slice.actions.setIsLoading({
                loadingField: 'isTicketDataFetching',
                isLoading: true
            })
        );
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/?request_type_code=${requestTypeCode}&request_type_key=${requestTypeKey}&page=${page}&page_size=${rowsPerPage}`;
        if (status !== 'All') {
            url += `&status=${status}`;
        }

        try {
            const response = await axios.get(url);
            dispatch(slice.actions.setAllTicketData(response.data));
        } catch (error) {
            const errorMessage = error?.response?.data?.message || 'Something went wrong';
            console.error(errorMessage);
        } finally {
            dispatch(
                slice.actions.setIsLoading({
                    loadingField: 'isTicketDataFetching',
                    isLoading: false
                })
            );
        }
    };
}

export function getOpenCloseTicketsData(
    pageChangeUrl = '',
    filterType = '',
    newPage = 0,
    isDirectLink = false,
    updateData = false,
    getAllTicketData = false
) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isDataLoading', isLoading: true }));
    return async () => {
        devConsolelog('getOpenCloseTicketsData url from params', pageChangeUrl, filterType, newPage);
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
            // if (newPage !== 0) dispatch(slice.actions.setCurrentPageUrl(pageChangeUrl));
        }
        if (filterType) {
            const finalFilterQueryString = filterQueryString(filterType);
            devConsolelog('filter url', finalFilterQueryString, filterType);
            url = `${url}?${finalFilterQueryString}`;
            // if (newPage === 0) dispatch(slice.actions.setCurrentPageUrl(url));
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });

            if (isDirectLink) {
                dispatch(slice.actions.setDynamicConversationObj({ ...response.data }));
                if (getAllTicketData) {
                    const { results = {} } = response.data || {};
                    const { open_close_system: ocSystem = {} } = results || {};
                    const { request_type_code: requestCode, request_type_key: requestType } = ocSystem?.[0] || {};
                    devConsolelog('response getOpenCloseTicketsData', response, getAllTicketData, requestCode, requestType);
                    dispatch(getAllTicketDataReducer(requestCode, requestType));
                }
                if (updateData) {
                    dispatch(slice.actions.setData({ ...response.data, page: newPage }));
                }
            } else {
                dispatch(slice.actions.setData({ ...response.data, page: newPage }));
            }
            dispatch(slice.actions.setIsLoading({ loadingField: 'isDataLoading', isLoading: false }));
        } catch (error) {
            devConsolelog('error ', error);
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isDataLoading', isLoading: false }));
        }
    };
}

export function getAutosuggestAssigneeData(str = '') {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/users?search=${str}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setAutosuggestAssigneeData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function postQueryData(updatedQueryData) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/`;
        try {
            const response = await axios.post(url, updatedQueryData, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            await dispatch(
                openSnackbar({
                    open: true,
                    message: `Query Added`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.hasError(''));
            window.location.reload();
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getSelectedObject(selectedObject) {
    return () => {
        dispatch(slice.actions.setSelectedTicketData({ ...selectedObject }));
    };
}

export function getCommentsData(ticketCode = '', nextPageUrl = '', isRefresh = false, stopLoading = false) {
    if (!nextPageUrl) {
        if (!stopLoading) dispatch(slice.actions.setIsLoading({ loadingField: 'isCommentLoading', isLoading: true }));
    }
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/${ticketCode}/conversations/`;
        if (nextPageUrl) url = nextPageUrl;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getCommentsData', response.data);
            dispatch(slice.actions.setCommentsData({ data: response.data, isRefresh }));
            if (!nextPageUrl) {
                dispatch(slice.actions.setIsLoading({ loadingField: 'isCommentLoading', isLoading: false }));
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            if (!nextPageUrl) {
                dispatch(slice.actions.setIsLoading({ loadingField: 'isCommentLoading', isLoading: false }));
            }
        }
    };
}

export function postComment(ticketCode = '', multipartFormData = {}) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/${ticketCode}/conversations/`;
        try {
            const response = await axios.post(url, multipartFormData, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getCommentsData ', multipartFormData, response.data);
            dispatch(slice.actions.setPostCommentsData({ ...response.data }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}
export function postInternalComment(internalCommentingCode = '', multipartFormData = {}) {
    const url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/${internalCommentingCode}/conversations/`;
    return async () => {
        try {
            const response = await axios.post(url, multipartFormData, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setPostInternalCommentsData({ ...response.data }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function patchCurrentTicketData(ticketId, updatedTicketData) {
    return async () => {
        devConsolelog('ticketId from params', ticketId, updatedTicketData);
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/${ticketId}/`;
        try {
            const response = await axios.patch(url, updatedTicketData, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            await dispatch(
                openSnackbar({
                    open: true,
                    message: `Ticket Status Updated`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            devConsolelog('response editTicketData', response.data);
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getBookingDetails(bookingCode = '') {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingDetailLoading', isLoading: true }));
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}bms/bookings/?booking_code=${bookingCode}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setBookingData({ ...response.data }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingDetailLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );

            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingDetailLoading', isLoading: false }));
        }
    };
}

export function getRequirementData(reqCode = '') {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isRequirementDetailLoading', isLoading: true }));
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/requirements/${reqCode}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setRequirementData({ ...response.data }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isRequirementDetailLoading', isLoading: false }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isRequirementDetailLoading', isLoading: false }));
        }
    };
}

export function getMemberData(memCode = '') {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isMemberDetailLoading', isLoading: true }));
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/${memCode}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setMemberDetails(response.data));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isMemberDetailLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isMemberDetailLoading', isLoading: false }));
        }
    };
}

export function getMemberCodeFromId(memberId = '') {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}members/${memberId}/get_code`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });

            const memberCode = response.data?.member_code;
            devConsolelog('response getMemberCode', response.data);
            return memberCode;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );

            return null;
        }
    };
}

export const getAdditionalFilterData = async (filterName = '') => {
    const url = `${ARYABHATTA_API_DOMAIN_V1}travsys/${filterName}`;
    try {
        const response = await axios.get(url);
        devConsolelog('response tags data', response.data);
        const { results = [] } = response.data;
        const data = { [filterName]: results };
        dispatch(slice.actions.setAdditionalFiltersData(data));
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        const { error: errorObj = {}, detail = '' } = error || {};
        const { errorMessage = '' } = errorObj || {};
        dispatch(
            openSnackbar({
                open: true,
                message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                variant: 'alert',
                alert: {
                    color: 'error'
                },
                close: false
            })
        );
    }
};

export const getTravelerDetailsInformation = (bookingCode = '', buyerCode = '') => {
    const url = `${ARYABHATTA_API_DOMAIN_V1}${BMS_CONSTANT}/${BOOKINGS}/${TRAVELERS_CONSTANT}/?booking_code=${bookingCode}&category=${BOOKING_STATUS_CODE_PACKAGE_BOOKING}&type=${BOOKING_TYPE_REMITTANCE}&buyer=${buyerCode}`;
    return async (dispatch) => {
        dispatch(setOnTripInformationLoading(true));
        axios
            .get(url)
            .then((response) => {
                const data = response?.data;
                const travelerNumber = data?.travellers?.filter((i) => i?.is_lead_traveller)?.[0]?.phone || '';
                dispatch(
                    setOnTripNumberAndTravelerInformation({
                        onTripNumber: data?.on_trip_number,
                        numberIsdCode: data?.isd_code,
                        travelerNumber,
                        isVip: data?.is_vip ?? false
                    })
                );
            })
            .catch((error) => {
                const errorResponse = error.response || {}; // Access the error response
                const errorData = errorResponse.data || {}; // Access the data in the error response
                const message = extractErrorMessage(errorData);
                dispatch(
                    openSnackbar({
                        open: true,
                        message: message || DEFAULT_ERROR_MESSAGE,
                        variant: 'alert',
                        alert: { color: 'error' },
                        close: false
                    })
                );
            })
            .finally(() => {
                dispatch(setOnTripInformationLoading(true));
            });
    };
};

export function getInternalCommentsConversation(
    internalConversationTicketCode = '',
    nextPageUrl = '',
    isRefresh = false,
    stopLoading = false
) {
    let url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/${internalConversationTicketCode}/conversations/`;

    return async (dispatch) => {
        if (!nextPageUrl) {
            if (!stopLoading) dispatch(setIsLoading({ loadingField: 'internalCommentsLoading', isLoading: true }));
        }
        if (nextPageUrl) url = nextPageUrl;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(setInternalCommentsData({ data: response.data, isRefresh }));
        } catch (error) {
            const errorResponse = error.response || {}; // Access the error response
            const errorData = errorResponse.data || {}; // Access the data in the error response
            const message = extractErrorMessage(errorData);
            dispatch(
                openSnackbar({
                    open: true,
                    message: message || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: false
                })
            );
        } finally {
            if (!nextPageUrl) {
                dispatch(setIsLoading({ loadingField: 'internalCommentsLoading', isLoading: false }));
            }
        }
    };
}

export const setLoadingState =
    (isLoading = {}) =>
    () => {
        dispatch(slice.actions.setIsLoading(isLoading));
    };

export const filterData =
    (filter = '') =>
    async () => {
        devConsolelog('in filters123', filter);
        dispatch(slice.actions.setFilters(filter || initialState.filters));
    };

export const clearData = () => () => {
    dispatch(slice.actions.setClearBookingData());
};
export const clearChatComments = () => () => {
    dispatch(slice.actions.clearChat());
};

export const updateChatList = () => () => {
    dispatch(slice.actions.clearLeftChatList());
};

export const confirmChildTicketPresent = (parentTicketCode = '') => {
    const url = `${ARYABHATTA_API_DOMAIN_V1}${ACM_CONSTANT}/${OC_SYSTEM_CONSTANT}/${parentTicketCode}/`;
    return async (dispatch) => {
        try {
            dispatch(setSelectedTicketDataChildTicketCodeFetchingLoader(true));
            const response = await axios.get(url);
            const data = response?.data || {};
            if (data?.linked_ticket) {
                dispatch(setSelectedTicketDataChildTicketCode(data?.linked_ticket));
            }
        } catch (err) {
            console.error(err);
        } finally {
            dispatch(setSelectedTicketDataChildTicketCodeFetchingLoader(false));
        }
    };
};

export const createChildTicketForParentCode = (formData) => {
    const url = `${ARYABHATTA_API_DOMAIN_V1}${ACM_CONSTANT}/${OC_SYSTEM_CONSTANT}/`;
    return async (dispatch) => {
        try {
            dispatch(setSelectedTicketDataChildTicketCodeFetchingLoader(true));
            const response = await axios.post(url, formData);
            const data = response?.data || {};
            if (data?.code) {
                dispatch(setSelectedTicketDataChildTicketCode(data?.code));
                dispatch(getInternalCommentsConversation(data?.code));
            }
        } catch (err) {
            console.error(err);
        } finally {
            dispatch(setSelectedTicketDataChildTicketCodeFetchingLoader(false));
        }
    };
};
